import { Box, Stack } from '@mui/system';
import GroupSettingsForm from './group-settings-form';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { useLocation, useParams } from 'react-router-dom';
import {
  getRoleOfGroupUserPortalQuery,
  groupSettingsQuery,
} from '../../../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import { userPortalPagesConfig } from '../../../../shared/user-portal-pages-config';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';
import SharedButton from '../../../../shared/buttons/shared-button';

export default function BusinessGroupSettings() {
  const { directoryRoles, changeLogRoles, loggedInUser } = useUserSettings();
  const location = useLocation();
  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );

  const { groupEmail } = useParams() as { groupEmail: string };

  const domain = groupEmail.split('@')[1];

  const {
    data: groupSettings,
    isLoading,
    isFetching,
  } = useQuery(groupSettingsQuery(groupEmail, userView));

  const { data: groupRole } = useQuery(
    getRoleOfGroupUserPortalQuery(userView, loggedInUser, groupEmail)
  );

  const isLoadingWithRefresh = isLoading || isFetching;

  if (!groupSettings || isLoadingWithRefresh) {
    return (
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ position: 'absolute', top: -60, right: 0 }}
        >
          <SharedIconButton variant="refresh" tooltipLabel="Refresh" disabled />
          <SharedButton variant="primary" disabled>
            Save
          </SharedButton>
        </Stack>
        <Stack sx={{ flex: 1, height: '100%' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
            General Information
          </Typography>
          <Stack sx={{ pt: 2, pl: 4 }}>
            <Skeleton variant="text" width="100%" height={50} />
          </Stack>
          <Stack flexDirection="row" sx={{ py: 2, px: 4 }} gap={14}>
            <Skeleton variant="text" width={400} height={80} />
            <Skeleton variant="text" width={400} height={80} />
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
            Directory
          </Typography>
          <Stack sx={{ py: 2, px: 4 }} gap={1}>
            <Skeleton variant="text" width="100%" height={80} />
            <Skeleton variant="text" width={400} height={80} />
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
            Content Control
          </Typography>

          <Stack
            sx={{
              py: 2,
              px: 4,
            }}
            gap={1}
          >
            <Skeleton variant="text" width="100%" height={80} />
            <Skeleton variant="text" width={400} height={80} />
          </Stack>

          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
            Settings
          </Typography>

          <Grid sx={{ p: 4 }} container spacing={4}>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
            Permissions
          </Typography>

          <Grid sx={{ p: 4 }} container spacing={4}>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Skeleton variant="text" width={400} height={80} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
            Advanced
          </Typography>

          <Stack
            sx={{
              py: 2,
              px: 4,
            }}
            gap={1}
          >
            <Skeleton variant="text" width="100%" height={100} />
            <Skeleton variant="text" width={400} height={80} />
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <GroupSettingsForm
      domain={domain}
      userView={userView}
      isUserMember={groupRole === 'MEMBER' || false}
      isUserManager={groupRole === 'MANAGER' || false}
      isUserOwner={groupRole === 'OWNER' || false}
      groupSettings={groupSettings}
      groupEmail={groupEmail}
      directoryRoles={directoryRoles}
      changeLogRoles={changeLogRoles}
    />
  );
}
